import React from 'react';
import { Phone, CreditCard, Users, Ambulance, Mail, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export function EmergencyInstructions() {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800/50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center flex-shrink-0">
                <Ambulance className="w-5 h-5 text-rose-500 dark:text-rose-400" />
              </div>
              <div className="text-left">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                  En cas d'urgence medicale
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {isExpanded ? 'Masquer' : 'Afficher'} les instructions
                </p>
              </div>
            </div>
            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full flex-shrink-0">
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              )}
            </div>
          </button>

          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-5 sm:px-6 pb-6 space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">1</span>
                    </div>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">
                      J'appelle le numero des urgences du pays ou je me trouve et je suis leurs instructions.
                    </p>
                  </div>

                  <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-8 h-8 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">2</span>
                      </div>
                      <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">
                        Je contacte la plateforme d'assistance Mutuaide (24h/24 et 7j/7)
                      </p>
                    </div>
                    <div className="ml-12 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center flex-shrink-0">
                          <Phone className="w-4 h-4 text-sky-500 dark:text-sky-400" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Globe PVT/EIC Canada</p>
                          <a href="tel:+33155987173" className="text-sm font-bold text-gray-900 dark:text-white">+33 (0)1 55 98 71 73</a>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center flex-shrink-0">
                          <Phone className="w-4 h-4 text-sky-500 dark:text-sky-400" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Globe PVT / WHV reste du monde</p>
                          <a href="tel:+33155987130" className="text-sm font-bold text-gray-900 dark:text-white">+33 (0)1 55 98 71 30</a>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-4 h-4 text-sky-500 dark:text-sky-400" />
                        </div>
                        <a href="mailto:medical@mutuaide.fr" className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                          medical@mutuaide.fr
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">
                      Je garde toujours sur moi la carte d'assure Globe PVT que le personnel soignant peut utiliser pour contacter Mutuaide.
                    </p>
                  </div>

                  <div className="p-4 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-100 dark:border-amber-800/30">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="space-y-2 text-sm text-amber-800 dark:text-amber-300">
                        <p className="font-semibold">Hospitalisation de plus de 24h :</p>
                        <p>Contactez Mutuaide pour ouvrir un dossier. Prise en charge directe possible et rapatriement medical si necessaire.</p>
                        <p className="font-semibold mt-3">Hospitalisation de moins de 24h :</p>
                        <p>Payez les frais et demandez un remboursement via la plateforme en ligne.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
